import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { API_ENDPOINTS } from '../config/api';

// Stripe Elements wrapper - get this from settings
const stripePromise = loadStripe('pk_test_51...'); // Will update from settings

const Donate = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [donationType, setDonationType] = useState('bank');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState('');
  const [stripeKey, setStripeKey] = useState('');

  const [heroImages, setHeroImages] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    fetchSettings();
    fetchHeroImages();
    
    // Auto-slide hero images
    const heroInterval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % (heroImages.length || 1));
    }, 5000);

    return () => {
      clearInterval(heroInterval);
    };
  }, [heroImages.length]);

  const fetchHeroImages = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.HERO_IMAGES, {
        params: { is_active: 1 }
      });
      if (response.data.success) {
        setHeroImages(response.data.data.map(img => img.image_path));
      } else {
        setHeroImages([
          '/images/hero1.jpg',
          '/images/hero2.jpg',
          '/images/hero3.jpg'
        ]);
      }
    } catch (error) {
      console.error('Error fetching hero images:', error);
      setHeroImages([
        '/images/hero1.jpg',
        '/images/hero2.jpg',
        '/images/hero3.jpg'
      ]);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.SETTINGS);
      if (response.data.success) {
        const settingsData = response.data.data || {};
        setSettings(settingsData);
        
        // Initialize stripe with settings
        const stripePubKey = settingsData.stripe_publishable_key || '';
        if (stripePubKey) {
          setStripeKey(stripePubKey);
        }
        
        // Set default donation type based on enabled gateways
        if (settingsData.mpesa_enabled === '1') {
          setDonationType('mpesa');
        } else if (settingsData.bank_transfer_enabled === '1') {
          setDonationType('bank');
        }
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load donation settings. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  const handlePayment = async (gateway) => {
    if (!settings) return;
    
    setIsProcessing(true);
    setError('');
    setSuccess('');

    try {
      const donationAmount = parseFloat(customAmount || amount);
      if (!donationAmount || donationAmount < parseFloat(settings.donation_min_amount || 1)) {
        throw new Error(`Minimum donation is ${settings.donation_min_amount || 1}`);
      }

      const payload = {
        gateway,
        amount: donationAmount,
        currency: settings.default_currency || 'USD',
        name,
        email,
        phone,
        is_recurring: isRecurring
      };

      const response = await axios.post(API_ENDPOINTS.DONATE, payload);

      if (response.data.success) {
        switch (gateway) {
          case 'paypal':
            window.location.href = response.data.approval_url;
            break;
            
          case 'stripe':
            setSuccess('Please complete card details below');
            break;
            
          case 'mpesa':
            setSuccess(response.data.response_description || 
                      'Check your phone to complete M-Pesa payment');
            break;
            
          default:
            setSuccess('Donation request submitted successfully');
        }
      } else {
        setError(response.data.error || 'Payment failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="donate-page">
        <div className="container py-5">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading donation options...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !settings) {
    return (
      <div className="donate-page">
        <div className="container py-5">
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Safely check enabled gateways
  const enabledGateways = {
    bank: settings?.bank_transfer_enabled === '1',
    paypal: settings?.paypal_enabled === '1',
    stripe: settings?.stripe_enabled === '1',
    mpesa: settings?.mpesa_enabled === '1'
  };

  // Get default values with fallbacks
  const getSetting = (key, defaultValue = '') => {
    return settings?.[key] || defaultValue;
  };

  return (
    <div className="donate-page">
      <style>{`
        .hero-section {
          min-height: 60vh;
          position: relative;
          overflow: hidden;
        }

        .hero-slider {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
        }

        .hero-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          transition: opacity 1.5s ease-in-out, transform 8s ease-out;
        }

        .hero-slide.active {
          animation: kenBurns 8s ease-out;
        }

        @keyframes kenBurns {
          0% { transform: scale(1); }
          100% { transform: scale(1.1); }
        }

        .slider-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: 2px solid white;
          background: transparent;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .slider-dot.active {
          background: white;
          transform: scale(1.2);
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="hero-section position-relative text-white">
        {/* Background Image Slider */}
        <div className="hero-slider">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                opacity: index === currentSlide ? 1 : 0,
                transform: index === currentSlide ? 'scale(1)' : 'scale(1.1)',
              }}
            />
          ))}
          
          {/* Overlay */}
          <div 
            className="position-absolute top-0 start-0 w-100 h-100"
            style={{
              background: 'linear-gradient(135deg, rgba(13, 110, 253, 0.85) 0%, rgba(108, 117, 125, 0.75) 100%)'
            }}
          />
        </div>

        {/* Content */}
        <div className="container position-relative" style={{ zIndex: 2, paddingTop: '100px', paddingBottom: '100px' }}>
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-3 fw-bold text-white mb-4">
                {getSetting('donation_page_title', 'Support Our Work')}
              </h1>
              <p className="lead text-white mb-0">
                {getSetting('donation_page_description', 
                  'Your donation helps build peaceful and resilient communities')}
              </p>
            </div>
          </div>
        </div>

        {/* Slider Indicators */}
        <div 
          className="position-absolute bottom-0 start-50 translate-middle-x mb-4"
          style={{ zIndex: 3 }}
        >
          <div className="d-flex gap-2">
            {heroImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Donation Impact */}
      <section className="py-5">
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center">
              <h2 className="display-5 fw-bold mb-4">Your Support Makes a Difference</h2>
              <p className="lead">
                Every donation contributes to promoting healthy, peaceful, equitable, inclusive 
                and resilient communities in North Rift Kenya and the Lower Karamoja Cluster.
              </p>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center h-100">
                <div className="card-body p-4">
                  <div className="mb-4">
                    <i className="bi bi-droplet display-4 text-primary"></i>
                  </div>
                  <h5 className="card-title">Water Projects</h5>
                  <p className="card-text text-muted">
                    {getSetting('donation_water_text', 
                      '$50 provides clean water for a family for a month')}
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center h-100">
                <div className="card-body p-4">
                  <div className="mb-4">
                    <i className="bi bi-book display-4 text-primary"></i>
                  </div>
                  <h5 className="card-title">Education</h5>
                  <p className="card-text text-muted">
                    {getSetting('donation_education_text', 
                      '$100 supports a child\'s education for one year')}
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card border-0 shadow-sm text-center h-100">
                <div className="card-body p-4">
                  <div className="mb-4">
                    <i className="bi bi-heart display-4 text-primary"></i>
                  </div>
                  <h5 className="card-title">Health Care</h5>
                  <p className="card-text text-muted">
                    {getSetting('donation_health_text', 
                      '$200 provides medical supplies for a health center')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Options */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4 p-lg-5">
                  <h2 className="h3 mb-4">Make a Donation</h2>
                  
                  {/* Donation Amount */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Donation Amount</label>
                    <div className="row g-2 mb-3">
                      {[25, 50, 100, 250, 500].map((amt) => (
                        <div className="col" key={amt}>
                          <button
                            type="button"
                            className={`btn w-100 ${amount === amt.toString() ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => {
                              setAmount(amt.toString());
                              setCustomAmount('');
                            }}
                          >
                            ${amt}
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Custom amount"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setAmount('');
                        }}
                        min={getSetting('donation_min_amount', 1)}
                      />
                    </div>
                  </div>

                  {/* Donor Information */}
                  <div className="mb-4">
                    <h5 className="mb-3">Your Information</h5>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Full Name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Email Address"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="col-md-6">
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="Phone Number (for M-Pesa)"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Recurring Donation Option */}
                  {getSetting('enable_recurring') === '1' && (
                    <div className="mb-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="recurring"
                          checked={isRecurring}
                          onChange={(e) => setIsRecurring(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="recurring">
                          <strong>Make this a monthly recurring donation</strong>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Payment Methods */}
                  <div className="mb-4">
                    <h5 className="mb-3">Payment Method</h5>
                    
                    {/* Payment Method Selector */}
                    <div className="btn-group w-100 mb-4" role="group">
                      {enabledGateways.bank && (
                        <button
                          type="button"
                          className={`btn ${donationType === 'bank' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDonationType('bank')}
                        >
                          <i className="bi bi-bank me-2"></i> Bank Transfer
                        </button>
                      )}
                      {enabledGateways.mpesa && (
                        <button
                          type="button"
                          className={`btn ${donationType === 'mpesa' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDonationType('mpesa')}
                        >
                          <i className="bi bi-phone me-2"></i> M-Pesa
                        </button>
                      )}
                      {enabledGateways.paypal && (
                        <button
                          type="button"
                          className={`btn ${donationType === 'paypal' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDonationType('paypal')}
                        >
                          <i className="fab fa-paypal me-2"></i> PayPal
                        </button>
                      )}
                      {enabledGateways.stripe && (
                        <button
                          type="button"
                          className={`btn ${donationType === 'stripe' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setDonationType('stripe')}
                        >
                          <i className="fas fa-credit-card me-2"></i> Card
                        </button>
                      )}
                    </div>

                    {/* Bank Transfer Details */}
                    {donationType === 'bank' && enabledGateways.bank && (
                      <div className="bank-details">
                        <h4 className="h5 mb-3">Direct Bank Transfer</h4>
                        <div className="bg-light p-4 rounded">
                          <div className="mb-3">
                            <label className="form-label fw-bold">Bank Name</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={getSetting('bank_name', 'Equity Bank Kenya Limited')}
                                readOnly
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => copyToClipboard(getSetting('bank_name'))}
                              >
                                <i className="bi bi-copy"></i>
                              </button>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-bold">Account Name</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={getSetting('account_name', 'SIKOM Peace Network for Development')}
                                readOnly
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => copyToClipboard(getSetting('account_name'))}
                              >
                                <i className="bi bi-copy"></i>
                              </button>
                            </div>
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-bold">Account Number</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={getSetting('account_number', '1234567890')}
                                readOnly
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => copyToClipboard(getSetting('account_number'))}
                              >
                                <i className="bi bi-copy"></i>
                              </button>
                            </div>
                          </div>

                          {getSetting('swift_code') && (
                            <div className="mb-3">
                              <label className="form-label fw-bold">SWIFT Code</label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={getSetting('swift_code')}
                                  readOnly
                                />
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => copyToClipboard(getSetting('swift_code'))}
                                >
                                  <i className="bi bi-copy"></i>
                                </button>
                              </div>
                            </div>
                          )}

                          {getSetting('bank_branch') && (
                            <div className="mb-3">
                              <label className="form-label fw-bold">Branch</label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={getSetting('bank_branch')}
                                  readOnly
                                />
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => copyToClipboard(getSetting('bank_branch'))}
                                >
                                  <i className="bi bi-copy"></i>
                                </button>
                              </div>
                            </div>
                          )}
                          
                          <div className="mt-3">
                            <button
                              className="btn btn-primary w-100"
                              onClick={() => handlePayment('bank')}
                              disabled={isProcessing || (!amount && !customAmount)}
                            >
                              {isProcessing ? 'Processing...' : 'Confirm Bank Transfer'}
                            </button>
                          </div>
                        </div>

                        <div className="alert alert-info mt-3">
                          <i className="bi bi-info-circle me-2"></i>
                          After making your bank transfer, please email us at {getSetting('contact_email', 'info@sikompeace.org')} 
                          with your name and transfer details so we can acknowledge your donation.
                        </div>
                      </div>
                    )}

                    {/* M-Pesa Details */}
                    {donationType === 'mpesa' && enabledGateways.mpesa && (
                      <div className="mpesa-details">
                        <h4 className="h5 mb-3">M-Pesa Transfer</h4>
                        <div className="bg-light p-4 rounded">
                          <div className="mb-4">
                            <p className="mb-3">Follow these steps to donate via M-Pesa:</p>
                            <ol className="ps-3">
                              <li className="mb-2">Go to M-Pesa on your phone</li>
                              <li className="mb-2">Select "Lipa Na M-Pesa"</li>
                              <li className="mb-2">Select "Pay Bill"</li>
                              <li className="mb-2">Enter Business Number: <strong>{getSetting('mpesa_shortcode', '123456')}</strong></li>
                              <li className="mb-2">Enter Account Number: <strong>DONATION</strong></li>
                              <li className="mb-2">Enter the amount you wish to donate</li>
                              <li>Enter your M-Pesa PIN and send</li>
                            </ol>
                          </div>

                          <div className="mb-3">
                            <label className="form-label fw-bold">PayBill Number</label>
                            <div className="input-group">
                              <input
                                type="text"
                                className="form-control"
                                value={getSetting('mpesa_shortcode', '123456')}
                                readOnly
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => copyToClipboard(getSetting('mpesa_shortcode'))}
                              >
                                <i className="bi bi-copy"></i>
                              </button>
                            </div>
                          </div>

                          <div className="mt-3">
                            <button
                              className="btn btn-success w-100"
                              onClick={() => handlePayment('mpesa')}
                              disabled={isProcessing || !phone || (!amount && !customAmount)}
                            >
                              {isProcessing ? 'Processing...' : 'Initiate M-Pesa Payment'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* PayPal Button */}
                    {donationType === 'paypal' && enabledGateways.paypal && (
                      <div className="paypal-details">
                        <div className="text-center">
                          <button
                            className="btn btn-paypal w-100 py-3"
                            onClick={() => handlePayment('paypal')}
                            disabled={isProcessing || (!amount && !customAmount)}
                            style={{backgroundColor: '#003087', color: 'white'}}
                          >
                            <i className="fab fa-paypal me-2 fs-4"></i>
                            {isProcessing ? 'Processing...' : 'Donate with PayPal'}
                          </button>
                          <small className="text-muted mt-2 d-block">
                            You will be redirected to PayPal to complete your donation
                          </small>
                        </div>
                      </div>
                    )}

                    {/* Stripe Card Payment */}
                    {donationType === 'stripe' && enabledGateways.stripe && stripeKey && (
                      <Elements stripe={loadStripe(stripeKey)}>
                        <StripePaymentForm
                          amount={customAmount || amount}
                          currency={getSetting('default_currency', 'USD')}
                          onSuccess={() => setSuccess('Payment successful!')}
                          onError={setError}
                          disabled={!amount && !customAmount}
                        />
                      </Elements>
                    )}
                  </div>

                  {/* Error/Success Messages */}
                  {error && (
                    <div className="alert alert-danger">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}
                  
                  {success && (
                    <div className="alert alert-success">
                      <i className="bi bi-check-circle me-2"></i>
                      {success}
                    </div>
                  )}

                  <div className="mt-4 text-center">
                    <p className="text-muted">
                      For other donation methods or corporate partnerships, please{' '}
                      <a href="/contact" className="text-primary">contact us</a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tax Benefits */}
      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="display-5 fw-bold mb-4">Your Donation is Tax Deductible</h2>
              <p className="lead mb-4">
                {getSetting('tax_deductible_info', 
                  'SIKOM Peace Network for Development is a registered NGO in Kenya. Your donations may be tax deductible depending on your country\'s laws.')}
              </p>
              <ul className="list-unstyled">
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Registered under the NGO Coordination Act, 1990
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Certificate of Registration: {getSetting('ngo_registration', 'OP.218/051/2006/0225/5382')}
                </li>
                <li>
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Annual audited financial statements available upon request
                </li>
              </ul>
            </div>
            
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-4">
                  <h5 className="card-title mb-3">Need a Receipt?</h5>
                  <p className="card-text">
                    We provide official donation receipts for all contributions. 
                    Please ensure you provide your contact details when donating 
                    or contact us after your donation.
                  </p>
                  <div className="mt-4">
                    <a href="/contact" className="btn btn-primary">
                      <i className="bi bi-envelope me-2"></i> Request Receipt
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Stripe Payment Form Component
const StripePaymentForm = ({ amount, currency, onSuccess, onError, disabled }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !amount) {
      return;
    }

    setProcessing(true);
    onError('');

    try {
      // Create payment intent via your API
      const response = await axios.post(API_ENDPOINTS.DONATE, {
        gateway: 'stripe',
        amount: parseFloat(amount),
        currency
      });

      if (!response.data.success) {
        throw new Error(response.data.error);
      }

      // Confirm payment
      const result = await stripe.confirmCardPayment(response.data.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (result.error) {
        onError(result.error.message);
      } else {
        onSuccess('Payment successful! Thank you for your donation.');
      }
    } catch (err) {
      onError(err.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label className="form-label">Card Details</label>
        <div className="border rounded p-3">
          <CardElement />
        </div>
      </div>
      
      <button
        type="submit"
        className="btn btn-primary w-100"
        disabled={!stripe || processing || !amount || disabled}
      >
        {processing ? 'Processing...' : `Donate $${amount}`}
      </button>
    </form>
  );
};

export default Donate;